import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import { TextField, Button, Box, Container, Grid, Alert, Typography } from '@mui/material';
import Cookies from '../common/Cookies.js';
import { useAuth, userFromDecodedJwt } from '../contexts/AuthContext';
import { useReseller } from '../contexts/ResellerContext';
import { API_URL, BASE_URL } from '../common/Constants';
import logoImage from '../assets/Chattitude_Main_Logo_v5.svg';
import LoadProgress from '../common/LoadProgress.js'
import SocialLogin from '../utils/sociallogin.js';
import TenantSelectionModal from './TenantSelectionModal';

export default function Login({ setLoggedIn }) {
  const { setUser } = useAuth();
  const { reseller, resellerResolved } = useReseller();
  const navigate = useNavigate();
  const location = useLocation();
  const [initialNext, setInitialNext] = useState(null);

  useEffect(() => {
    try {
      // If a next query param is present in location.search, prefer that.
      const params = new URLSearchParams(location.search || '');
      let nextParam = params.get('next');
      if (!nextParam) {
        // Otherwise capture the hash (e.g. #/chat-logs/...), unless it's the Login page itself
        const hash = window.location.hash || '';
        const hashPath = (hash.split('?')[0] || '').toString();
        if (hashPath && !hashPath.startsWith('#/Login') && !hashPath.startsWith('#/')) {
          // nothing - ensure startsWith '#/' to capture
        }
        if (hashPath && hashPath.startsWith('#/')) {
          setInitialNext(encodeURIComponent(hash));
        }
      } else {
        setInitialNext(nextParam);
      }
    } catch (e) {
      // ignore
    }
    // run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [alert, setAlert] = useState(null);
  const [progressShow, setProgressShow] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // Agency tenant selection state
  const [agencyTenants, setAgencyTenants] = useState([]);
  const [agencyToken, setAgencyToken] = useState(null);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [tenantSelectLoading, setTenantSelectLoading] = useState(false);

  const completeLogin = useCallback((jwtToken) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
    Cookies.setJwtToken(jwtToken);
    var token = atob(jwtToken.split(".")[1]);
    var jsonToken = JSON.parse(token);
    Cookies.setTenantId(jsonToken.tenantId);
    Cookies.setCookies('fname', jsonToken.firstName);
    Cookies.setCookies('lname', jsonToken.lastName);
    Cookies.setCookies('email', jsonToken.email);
    Cookies.setCookies('hasAgent', jsonToken.hasAgent);
    if (jsonToken.agencyId) {
      Cookies.setCookies('agencyId', jsonToken.agencyId);
    }

    try {
      let nextParam = null;
      const params = new URLSearchParams(location.search || '');
      nextParam = params.get('next');
      if (!nextParam) {
        const hashQuery = window.location.hash.split('?')[1] || '';
        if (hashQuery) {
          const hashParams = new URLSearchParams(hashQuery);
          nextParam = hashParams.get('next');
        }
      }
      if (!nextParam && initialNext) nextParam = initialNext;
      if (nextParam) {
        const decoded = decodeURIComponent(nextParam);
        if (decoded.startsWith('#/') || decoded.startsWith('#')) {
          const target = `${window.location.origin}${window.location.pathname}#${decoded.replace(/^#/, '')}`;
          window.location.href = target;
          setTimeout(() => {
            try {
              setUser(userFromDecodedJwt(jsonToken));
              setLoggedIn(true);
              if (decoded.startsWith('#')) {
                window.location.hash = decoded.replace(/^#/, '');
              } else {
                navigate(decoded, { replace: true });
              }
            } catch (e) { /* ignore */ }
          }, 800);
          return;
        } else if (decoded.startsWith('http://') || decoded.startsWith('https://')) {
          window.location.href = decoded;
          setTimeout(() => {
            try {
              setUser(userFromDecodedJwt(jsonToken));
              setLoggedIn(true);
            } catch (e) { /* ignore */ }
          }, 800);
          return;
        } else {
          try {
            setUser(userFromDecodedJwt(jsonToken));
          } catch (e) { /* ignore */ }
          setLoggedIn(true);
          setAlert({ severity: 'success', message: `Welcome ${jsonToken.firstName} ${jsonToken.lastName}` });
          navigate(decoded.startsWith('/') ? decoded : `/${decoded}`, { replace: true });
          return;
        }
      }
    } catch (e) { /* fall through */ }

    try {
      setUser(userFromDecodedJwt(jsonToken));
    } catch (e) { /* ignore */ }
    setLoggedIn(true);
    setAlert({ severity: 'success', message: `Welcome ${jsonToken.firstName} ${jsonToken.lastName}` });
    navigate('/Dashboard/true', { replace: true });
  }, [location, initialNext, setUser, setLoggedIn, navigate, setAlert]);

  const handleTenantSelect = (tenantId) => {
    setTenantSelectLoading(true);
    axios.post("/login/select-tenant", null, { params: { token: agencyToken, tenantId } })
      .then((res) => {
        if (res.data && res.data.status === 'success') {
          setShowTenantModal(false);
          completeLogin(res.data.body);
        } else {
          setAlert({ severity: 'error', message: res.data?.body || 'Failed to select tenant' });
        }
      })
      .catch(() => {
        setAlert({ severity: 'error', message: 'Error selecting tenant' });
      })
      .finally(() => {
        setTenantSelectLoading(false);
      });
  };

  const handleSubmit = (event) => {
    // avoid logging credentials in console
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity()) {
      setProgressShow(true);
      axios.post("/login/validate", null, { params: { email: email, password: password } })
        .then((res) => {
          if (res.data) {
            if (res.data.status === 'agency_select') {
              setAgencyTenants(res.data.body.tenants || []);
              setAgencyToken(res.data.body.token);
              setShowTenantModal(true);
            } else if (res.data.status === 'success') {
              completeLogin(res.data.body);
            } else {
              setAlert({ severity: res.data.status, message: res.data.body });
            }
          }
        })
        .catch((res) => {
          console.log(res);
          setAlert({ severity: 'error', message: 'Getting error at the server' });
        })
        .finally(() => {
          setProgressShow(false);
        })
    }
  }

  const handleForgotSubmit = (e) => {
    e.preventDefault();
    setForgotError('');
    const baseUrl = window.location.origin;
    axios.post("/users/request-password-reset", { email: forgotEmail, baseUrl })
      .then(() => {
        setForgotSent(true);
      })
      .catch((error) => {
        if (error.response?.status === 400 && typeof error.response.data?.message === 'string'
          && error.response.data.message) {
          setForgotError(error.response.data.message);
          return;
        }
        setForgotSent(true);
      });
  }

  return (
    <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <br />
      {
        alert &&
        <Alert severity={alert.severity}>{alert.message}</Alert>
      }
      <br />
      <Paper elevation={4}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid
            container
            direction="column"
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Grid
              direction="column"
              sx={{
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div style={{ margin: '10px', minHeight: 80 }}>
                {resellerResolved ? (
                  <img
                    src={
                      reseller?.logoPath
                        ? reseller.logoPath.startsWith('http://') || reseller.logoPath.startsWith('https://')
                          ? reseller.logoPath
                          : `${BASE_URL}/api/public/reseller/${reseller.id}/logo`
                        : logoImage
                    }
                    alt={reseller?.name || 'Powered With Chattitude'}
                    width="400"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = logoImage;
                    }}
                  />
                ) : (
                  <div style={{ width: 400, maxWidth: '100%', height: 72 }} aria-hidden />
                )}
              </div>
              <TextField
                label="Email" required autoFocus type="email"
                variant="outlined"
                margin="normal" fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                label="Password" required
                variant="outlined" fullWidth
                margin="normal"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <br /><br />
              <Button variant="contained" color="primary" type="submit">
                Login
              </Button>
              <br />
              <br />

              {/* <SocialLogin /> */}

            </Grid>
          </Grid>
        </Box>
      </Paper>
      <div style={{ marginTop: 10 }}>
        <button type="button" onClick={() => setShowForgot(true)} style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}>
          Forgot Password?
        </button>
      </div>

      {showForgot && (
        <Paper elevation={1} sx={{ mt: 2, p: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Forgot your password?
          </Typography>
          {forgotSent ? (
            <div>If your email is registered, you’ll receive a reset link.</div>
          ) : (
            <form onSubmit={handleForgotSubmit}>
              {forgotError && <Alert severity="error">{forgotError}</Alert>}
              <TextField
                type="email"
                placeholder="Enter your email"
                label="Email"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                required
                fullWidth
                variant="outlined"
                margin="normal"
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" color="primary" type="submit">
                  Send Reset Link
                </Button>
                <Button variant="outlined" color="primary" type="button" onClick={() => setShowForgot(false)}>
                  Cancel
                </Button>
              </Box>
            </form>
          )}
        </Paper>
      )}
      <TenantSelectionModal
        open={showTenantModal}
        tenants={agencyTenants}
        onSelect={handleTenantSelect}
        loading={tenantSelectLoading}
      />
      <LoadProgress open={progressShow}></LoadProgress>
    </Container>
  );
}
